import React from "react";
import { RouteComponentProps } from "react-router";
import { Alert, Button, Col, Collapse, Divider, message, Row, Spin, Table, Tooltip } from "antd";
import {
  ClockCircleFilled,
  CloseCircleFilled,
  LeftOutlined,
  LinkOutlined,
  ReloadOutlined,
  RightOutlined,
} from "@ant-design/icons";
import Card from "../components/Card";
import {
  NotificationRecord,
  NotificationStatusTypesEnum,
  NotificationTypesEnum,
} from "../helpers/shared-interfaces";
import { request } from "../helpers/utilities";
import { AxiosResponse } from "axios";
import * as Sentry from "@sentry/browser";
import ReactHTMLParser from "react-html-parser";
import { notificationColumns } from "./NotificationRecords";
import { NavLink } from "react-router-dom";
import { CHECK_EMAIL_DELIVERY_STATUS, GET_NOTIFICATION_RECORD } from "../gql/notification-records";
import CollapsePanel from "antd/lib/collapse/CollapsePanel";
import ModalCreator from "../components/ModalCreator";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  isLoading: boolean;
  id: string | number;
  notificationRecord?: NotificationRecord;
  currentNotificationRecord: number;
  notificationType?: string
  showModal: boolean,
  selectedAttachment: any,
}

interface RouteParams {
  id: string;
}

interface Props
  extends RouteComponentProps<
    RouteParams,
    any,
    { type: string; notificationRecords?: NotificationRecord[] }
  > { }

type StatusTone = "error" | "warning";

const STATUS_TONES: Record<
  StatusTone,
  { accent: string; title: string; body: string; shadow: string }
> = {
  error: {
    accent: "#cf1322",
    title: "#cf1322",
    body: "#5c0011",
    shadow: "rgba(207, 19, 34, 0.08)",
  },
  warning: {
    accent: "#d48806",
    title: "#d48806",
    body: "#613400",
    shadow: "rgba(212, 136, 6, 0.08)",
  },
};

interface StatusBannerProps {
  tone: StatusTone;
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
  action?: React.ReactNode;
}

export function StatusBanner({
  tone,
  icon,
  title,
  description,
  action,
}: StatusBannerProps): JSX.Element {
  const palette = STATUS_TONES[tone];
  return (
    <Alert
      type={tone}
      showIcon
      icon={icon}
      style={{
        borderLeft: `4px solid ${palette.accent}`,
        borderRadius: "6px",
        padding: "16px 20px",
        alignItems: "center",
        boxShadow: `0 1px 2px ${palette.shadow}`,
      }}
      message={
        <span
          style={{
            fontSize: "15px",
            fontWeight: 600,
            color: palette.title,
          }}
        >
          {title}
        </span>
      }
      description={
        <span style={{ color: palette.body, lineHeight: 1.5 }}>
          {description}
        </span>
      }
      action={action}
    />
  );
}

export function getExtendedNotificationColumns(notificationType?: string) {
  let extendedColumns: any = []
  if (window.location.pathname.includes("/notification-records/")) {
    extendedColumns = notificationColumns.filter((col) => col.key !== "notifierRecord")
  } else {
    extendedColumns = [...notificationColumns]
  }

  if (notificationType === "redemption") {
    extendedColumns.push({
      title: "Link To Redemption Request",
      dataIndex: "redemptions",
      key: "redemptions",
      width: "30%",
      render(text: string, record: NotificationRecord): JSX.Element {
        return (
          <NavLink to={`/redemption-requests/${record.redemptionRequest?.id}`}>
            <LinkOutlined />
          </NavLink>
        );
      },
    });
  } else if (notificationType === "transfer") {
    extendedColumns.push({
      title: "Link To Transfer Request",
      dataIndex: "transfers",
      key: "transfers",
      width: "30%",
      render(text: string, record: NotificationRecord): JSX.Element {
        return (
          <NavLink to={`/transfer-requests/${record.transferRequest?.id}`}>
            <LinkOutlined />
          </NavLink>
        );
      },
    });
  } else if (notificationType === "onlineTransaction") {
    extendedColumns.push({
      title: "Link To Online Transaction",
      dataIndex: "transactions",
      key: "transactions",
      width: "30%",
      render(text: string, record: NotificationRecord): JSX.Element {
        return (
          <NavLink to={`/online-transactions/${record.customerTransaction?.id}`}>
            <LinkOutlined />
          </NavLink>
        );
      },
    });
  } else if (notificationType === "opportunity") {
    extendedColumns.push({
      title: "Link To Opportunity",
      dataIndex: "opportunity",
      key: "opportunity",
      width: "30%",
      render(text: string, record: NotificationRecord): JSX.Element {
        return (
          <NavLink to={`/business-development/opportunity/${record.opportunity?.id}`}>
            <LinkOutlined />
          </NavLink>
        );
      },
    });
  }

  return extendedColumns;
}

export function NotificationRecordTableFactory(
  data: NotificationRecord[],
  notificationType?: string
): JSX.Element {
  const tempData: NotificationRecord[] = data.map(
    (record: NotificationRecord): NotificationRecord => {
      return { ...record, key: record.id };
    },
  );
  const columns = getExtendedNotificationColumns(notificationType);

  return (
    <Table
      pagination={false}
      bordered={true}
      columns={columns}
      dataSource={tempData}
    />
  );
}

export default class ViewNotificationRecord extends React.Component<
  Props,
  State
> {
  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      isLoading: true,
      id: "",
      notificationRecord: undefined,
      currentNotificationRecord: 0,
      showModal: false,
      selectedAttachment: null,
    };

    this.getNotificationRecordData = this.getNotificationRecordData.bind(this);
  }

  redirectToAllAuditsPage = (): void => {
    this.props.history.push("/audits");
  };

  getNotificationRecordData(): void {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_NOTIFICATION_RECORD,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            const notificationRecord = data.data.notifierRecord as NotificationRecord;

            let notificationType = "";

            if (notificationRecord.transferRequest) {
              notificationType = "transfer";
            } else if (notificationRecord.redemptionRequest) {
              notificationType = "redemption";
            } else if (notificationRecord.customerTransaction) {
              notificationType = "onlineTransaction";
            } else if (notificationRecord.opportunity) {
              notificationType = "opportunity";
            }

            this.setState({
              notificationRecord,
              notificationType
            });
          } else {
            message.error("Failed to load notification data");
          }
        });
      }
    );
  }

  checkDeliveryStatus = async (): Promise<void> => {
    const { id } = this.state;
    if (!id) return;

    this.setState({ isLoading: true });
    try {
      const response: AxiosResponse<any> = await request({
        query: CHECK_EMAIL_DELIVERY_STATUS,
        variables: { id },
      });
      const result = response.data?.data?.checkEmailDeliveryStatus;
      const errors = result?.errors;
      if (errors && errors.length > 0) {
        message.error(errors[0].description || "Failed to refresh status");
        this.setState({ isLoading: false });
        return;
      }
      message.success("Status refreshed");
      this.getNotificationRecordData();
    } catch (error) {
      Sentry.captureException(error);
      message.error(`Failed to refresh status - ${generateDisplayErrorMessage(error)}`);
      this.setState({ isLoading: false });
    }
  };

  componentDidMount(): void {
    this.setState(
      {
        id: this.props.match.params.id,
        currentNotificationRecord:
          this.props?.location?.state?.notificationRecords?.findIndex(
            (item) =>
              item?.id.toString() === this.props.match.params.id.toString(),
          ) ?? 0,
      },
      (): void => {
        this.getNotificationRecordData();
      },
    );
  }

  toggleShowModal = (attachment: any) => {
    this.setState((prevState) => ({
      selectedAttachment: attachment,
      showModal: !prevState.showModal,
    }));
  };

  render(): JSX.Element | null {
    const { isLoading, notificationRecord, currentNotificationRecord, notificationType, selectedAttachment, showModal } =
      this.state;

    const moveToNextLink = `/notification-records/${this.props.location?.state?.notificationRecords?.[
      currentNotificationRecord + 1
    ]?.id
      }`;

    const moveToPreviousLink = `/notification-records/${this.props.location?.state?.notificationRecords?.[
      currentNotificationRecord - 1
    ]?.id
      }`;

    const nextPreviousUI = (
      <>
        <br />
        <Row
          gutter={10}
          style={{
            float: "right",
            marginTop: "10px",
          }}
        >
          <Col>
            {this.state.currentNotificationRecord > 0 && (
              <NavLink
                to={{
                  pathname: moveToPreviousLink,
                  state: {
                    type: "notificationRecord",
                    notificationRecords:
                      this.props?.location?.state?.notificationRecords ?? [],
                  },
                }}
              >
                <Tooltip placement="left" title={"Previous"}>
                  <LeftOutlined style={{ color: "#1D3764" }} />
                </Tooltip>
              </NavLink>
            )}
          </Col>

          <Col>
            {this.props?.location?.state?.notificationRecords &&
              this.state.currentNotificationRecord <
              this.props?.location?.state?.notificationRecords?.length -
              1 && (
                <NavLink
                  to={{
                    pathname: moveToNextLink,
                    state: {
                      type: "notificationRecord",
                      notificationRecords:
                        this.props?.location?.state?.notificationRecords ?? [],
                    },
                  }}
                >
                  <Tooltip placement="right" title={"Next"}>
                    <RightOutlined style={{ color: "#1D3764" }} />
                  </Tooltip>
                </NavLink>
              )}
          </Col>

          <Col>
            {this.props?.location?.state?.notificationRecords &&
              `${currentNotificationRecord + 1} / ${this.props?.location?.state?.notificationRecords?.length
              }`}
          </Col>
        </Row>
      </>
    );

    const status = notificationRecord?.status;
    const showRejectionReason = status === NotificationStatusTypesEnum.failed;
    const showDeliveryInProgress =
      notificationRecord?.notificationType === NotificationTypesEnum.email &&
      (status === NotificationStatusTypesEnum.pending ||
        status === NotificationStatusTypesEnum.queued);
    const inProgressCopy =
      status === NotificationStatusTypesEnum.queued
        ? "queued for delivery"
        : "pending delivery";

    return (
      <Spin spinning={isLoading}>
        <Row>{notificationRecord && <Col span={24}>{nextPreviousUI}</Col>}</Row>
        <Card>
          <Row>
            <Col span={24}>
              {notificationRecord &&
                NotificationRecordTableFactory([notificationRecord], notificationType)}
            </Col>

            <Divider />

            {showRejectionReason && (
              <Col span={24} style={{ marginBottom: "20px" }}>
                <StatusBanner
                  tone="error"
                  icon={
                    <CloseCircleFilled
                      style={{ fontSize: "24px", color: STATUS_TONES.error.accent }}
                    />
                  }
                  title="Rejection Reason"
                  description={
                    notificationRecord?.rejectReason ||
                    "No rejection reason was returned by the provider."
                  }
                />
              </Col>
            )}

            {showDeliveryInProgress && (
              <Col span={24} style={{ marginBottom: "20px" }}>
                <StatusBanner
                  tone="warning"
                  icon={
                    <ClockCircleFilled
                      style={{ fontSize: "24px", color: STATUS_TONES.warning.accent }}
                    />
                  }
                  title="Delivery In Progress"
                  description={
                    <>
                      This notification is still {inProgressCopy}. Click Reload
                      Status to re-check with the provider.
                    </>
                  }
                  action={
                    <Tooltip title="Re-check delivery status with the provider">
                      <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        onClick={this.checkDeliveryStatus}
                        style={{
                          backgroundColor: STATUS_TONES.warning.accent,
                          borderColor: STATUS_TONES.warning.accent,
                          boxShadow: "0 2px 4px rgba(212, 136, 6, 0.25)",
                          fontWeight: 500,
                        }}
                      >
                        Reload Status
                      </Button>
                    </Tooltip>
                  }
                />
              </Col>
            )}

            {notificationRecord && (
              <Col span={24}>
                <span style={{ fontWeight: "bold" }}>Message Details</span>
                <div
                  style={{
                    border: "1px solid #f0f0f0",
                    boxSizing: "border-box",
                    padding: "15px",
                    marginTop: "15px",
                  }}
                >
                  {ReactHTMLParser(notificationRecord.details)}
                </div>
                {notificationRecord.attachments && notificationRecord.attachments.length > 0 && (
                  <Collapse style={{ marginTop: "15px" }}>
                    <CollapsePanel header="Attachments" key="1">
                      {notificationRecord.attachments.map((attachment, index) => (
                        <p key={index} style={{ color: "#1890ff", cursor: "pointer" }} onClick={() => this.toggleShowModal(attachment)}>
                          {attachment.name}
                        </p>
                      ))}
                    </CollapsePanel>
                  </Collapse>
                )}
              </Col>
            )}
          </Row>
        </Card>
        {ModalCreator({
          title: selectedAttachment?.name,
          okType: "primary",
          showModal: showModal,
          isLoading: false,
          width: 1000,
          onOkay: () => this.toggleShowModal(undefined),
          toggleModal: () => this.toggleShowModal(undefined),
          showFooter: false,
          maskClosable: true,
          inscription: selectedAttachment && (
            <iframe
              title={selectedAttachment?.name || "Attachment Preview"}
              src={`data:${selectedAttachment.type};base64,${selectedAttachment.content}`}
              width="100%"
              height="500px"
            />
          )
        })}
      </Spin>
    );
  }
}
